import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale, API_BASE } from '../../lib/api';
import ResponsiveImage from './ResponsiveImage';
import { GALLERY_SIZES } from '../../lib/responsive';

export default function LooksHallOfFame() {
  const { content, locale } = useContent();
  const data = content.looks_hall_of_fame || {};
  const cmsItems = data.items || [];
  const [mediaItems, setMediaItems] = useState([]);

  // Media Library fallback when no custom items in the CMS.
  useEffect(() => {
    if (cmsItems.length > 0) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/media/by-section/looks_hall_of_fame.items?limit=8`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setMediaItems((d.items || []).map((a) => ({
          name: { es: a.alt_text_es || a.original_name, en: a.alt_text_en || a.original_name },
          base_image: a.thumb_url || a.url,
          hover_image: a.url,
          variants: a.variants,
          variantsAvif: a.variants_avif,
          year: new Date(a.created_at || Date.now()).getFullYear().toString(),
          _key: a.id,
        })));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cmsItems]);

  const items = cmsItems.length > 0 ? cmsItems : mediaItems;

  const scrollTo = (target) => {
    if (!target) return;
    if (target.startsWith('#')) {
      document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.open(target, '_blank');
    }
  };

  return (
    <section
      id="looks"
      data-testid="looks-section"
      className="section bg-bg overflow-hidden"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-20">
          <div className="md:col-span-7">
            <p className="text-eyebrow mb-4">{pickLocale(data.eyebrow, locale)}</p>
            <h2 className="text-block block-huge text-ink leading-[0.86] mb-2" data-testid="looks-title">
              {pickLocale(data.title, locale)}
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col justify-end">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed">
              {pickLocale(data.description, locale)}
            </p>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-testid="looks-grid">
          {items.map((it, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: (i % 8) * 0.04 }}
              className="group cursor-pointer"
              data-testid={`look-card-${i}`}
            >
              <div className="swap-card relative w-full aspect-[3/4] bg-bg-secondary border border-line overflow-hidden">
                {it.base_image && (
                  <ResponsiveImage
                    src={it.base_image.startsWith('http') ? it.base_image : `${API_BASE}${it.base_image}`}
                    variants={it.variants}
                    variantsAvif={it.variantsAvif}
                    sizes={GALLERY_SIZES}
                    alt={pickLocale(it.name, locale)}
                    loading="lazy"
                    className="swap-base absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {it.hover_image && (
                  <ResponsiveImage
                    src={it.hover_image.startsWith('http') ? it.hover_image : `${API_BASE}${it.hover_image}`}
                    variants={it.variants}
                    variantsAvif={it.variantsAvif}
                    sizes={GALLERY_SIZES}
                    alt={pickLocale(it.name, locale) + ' alt'}
                    loading="lazy"
                    className="swap-hover absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {/* Year badge */}
                <span className="absolute top-3 right-3 caption-pill !text-[9px] !px-2 !py-1">
                  {it.year}
                </span>
                {/* Hover acid line */}
                <div className="absolute inset-x-0 bottom-0 h-0 bg-acid transition-all duration-500 group-hover:h-1" />
              </div>
              <figcaption className="mt-3 flex items-baseline justify-between">
                <h3 className="font-display text-xl md:text-2xl text-ink leading-tight group-hover:text-acid transition-colors">
                  {pickLocale(it.name, locale)}
                </h3>
                <span className="text-eyebrow text-ink-dim">{it.year}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {data.cta_label && (
          <div className="mt-12 md:mt-16 flex justify-center">
            <button
              onClick={() => scrollTo(data.cta_url)}
              data-testid="looks-cta"
              className="btn-ghost group"
            >
              {pickLocale(data.cta_label, locale)} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
